/**
 * A Supabase Storage photograph at the width a surface draws it (2026-10-01).
 *
 * The town stills (asset-library/photos/grok-imagine/imagine-place-city-*.png)
 * are 2.6 to 2.9 MB PNGs. The homepage drew them as 56px search thumbnails and
 * as mosaic tiles at full size: six of them were 16 MB of a page, and in the
 * opened search the last thumbnail had not arrived 700 ms after the click
 * (build b1, Terrebonne). Supabase's image renderer serves the same object
 * at a width, as WebP to a browser that accepts it (measured: 160px wide,
 * 11 KB; 720px wide, 78 KB). The admin photo board already reads it this way.
 *
 * Pure: a Supabase Storage public object URL comes back as its render URL at
 * `width`; anything else (a local /images path, a listing CDN) comes back as
 * it was. Never invents a source.
 */
const OBJECT = '/storage/v1/object/public/'
const RENDER = '/storage/v1/render/image/public/'

export function storageImageAtWidth(
  url: string | null | undefined,
  width: number,
  quality = 72,
): string | null {
  const src = url?.trim()
  if (!src) return null
  if (!Number.isFinite(width) || width <= 0) return src
  let parsed: URL
  try {
    parsed = new URL(src)
  } catch {
    return src
  }
  if (!parsed.hostname.endsWith('.supabase.co') || !parsed.pathname.startsWith(OBJECT)) return src
  if (parsed.search) return src
  parsed.pathname = RENDER + parsed.pathname.slice(OBJECT.length)
  parsed.search = `?width=${Math.round(width)}&quality=${Math.round(quality)}`
  return parsed.toString()
}

/**
 * The same object as an `<img srcset>` across `widths` (2026-10-22 audit: the
 * Stage drew the 2.9 MB town PNG as its poster; the renderer serves it as a
 * ~190 KB WebP at full width). The renderer never upscales, so a width past the
 * master returns the master. Null for anything storageImageAtWidth would leave
 * as it was, so the caller keeps its plain src.
 */
export function storageImageSrcSet(
  url: string | null | undefined,
  widths: readonly number[],
  quality = 72,
): string | null {
  const src = url?.trim()
  if (!src || widths.length === 0) return null
  if (storageImageAtWidth(src, widths[0], quality) === src) return null
  return widths.map((w) => `${storageImageAtWidth(src, w, quality)} ${Math.round(w)}w`).join(', ')
}

/**
 * A full-bleed poster (Stage, place opening). The town stills are 1536px
 * masters, so `src` is the master width and 2048 returns the master for them
 * while staying sharp for a larger library photo on a 2x desktop.
 */
const POSTER_WIDTHS = [640, 1080, 1536, 2048] as const
const POSTER_SRC_WIDTH = 1536
const POSTER_QUALITY = 80

export function storagePosterSources(url: string): { src: string; srcSet?: string } {
  const srcSet = storageImageSrcSet(url, POSTER_WIDTHS, POSTER_QUALITY)
  if (!srcSet) return { src: url }
  return { src: storageImageAtWidth(url, POSTER_SRC_WIDTH, POSTER_QUALITY) ?? url, srcSet }
}
